import { format } from "date-fns";
import { z } from "zod";

import { WorkflowTodoSortBySchema } from "@performyard/service-contracts";

import type { SortDirection } from "../../api/hooks";

import type { WorkflowTodoSortBy } from "../../api/services/defs/workflows";

/**
 * Shared URL-state sanitizers for the workflow to-do pages (All To-Dos and the
 * workflow details page). Everything here runs on values that came out of the
 * query string, so it must treat its input as untrusted: an old bookmark from the
 * legacy pages, a hand-edited link, or a stale share.
 */

const OBJECT_ID_PATTERN = /^[0-9a-fA-F]{24}$/;

export const isObjectIdString = (value: string): boolean => OBJECT_ID_PATTERN.test(value);

export const MAX_SEARCH_LENGTH = 256;

/** An employee/workflow reference as the filter panels and chips store it. */
export const NamedReferenceSchema = z.object({
	id: z.string().regex(OBJECT_ID_PATTERN),
	name: z.string(),
});

export type NamedReference = z.infer<typeof NamedReferenceSchema>;

export const isNamedReference = (value: unknown): value is NamedReference =>
	NamedReferenceSchema.safeParse(value).success;

// Runs before the query-state hook converts "date-..." strings back into Dates,
// so date keys are validated as serialized strings and passed through untouched.
const SerializedDateSchema = z
	.string()
	.startsWith("date-")
	.refine((value) => !Number.isNaN(new Date(value.replace("date-", "")).getTime()));

export const isSerializedDate = (value: unknown): value is string => SerializedDateSchema.safeParse(value).success;

// The sort params live in the URL, so they can hold arbitrary values (old bookmarks
// from the legacy page used other names). Fall back to the defaults instead of
// sending an invalid sort the API would reject.
export const sanitizeSortBy = (value: string): WorkflowTodoSortBy =>
	WorkflowTodoSortBySchema.catch("due_date").parse(value);

export const sanitizeSortDirection = (value: string): SortDirection =>
	z.enum(["asc", "desc"]).catch("asc").parse(value);

/**
 * Page numbers are 1-based. A hand-edited or stale `?pageNumber=` can hold a
 * negative or fractional value, which would reach the API as a negative or
 * non-integer `skip` and come back as a 422 the page renders as "there was a
 * problem loading" — an outage message for a bad URL.
 */
export const sanitizePageNumber = (value: number): number => (Number.isInteger(value) && value >= 1 ? value : 1);

export const toDateOnlyParam = (value?: Date): string | undefined => (value ? format(value, "yyyy-MM-dd") : undefined);
